import { StatusBadge } from "@/components/ui/StatusBadge";
import {
  DEFAULT_IMAGE,
  getPropertyImageUrl,
  isPlaceholder,
  markAsPlaceholder,
} from "@/lib/config/cdn";
import { Listing } from "@/lib/types";
import { Heart } from "lucide-react";
import { memo } from "react";

interface PropertyImageProps {
  property: Listing;
  isFeatured?: boolean;
  isBoosted?: boolean;
  isCompact?: boolean;
  isFavorite: boolean;
  onToggleFavorite: (e: React.MouseEvent) => void;
}

// Memoize component to prevent unnecessary rerenders
const PropertyImage = memo(
  ({
    property,
    isFeatured = false,
    isBoosted = false,
    isCompact = false,
    isFavorite,
    onToggleFavorite,
  }: PropertyImageProps) => {
    // Get image URL from CDN using the listing code (prioritize listing_code over listingCode)
    const imageUrl = getPropertyImageUrl(
      property.listing_code || property.listingCode
    );

    return (
      <div className={`relative flex-shrink-0`}>
        <img
          src={imageUrl}
          alt={property.title || "Commercial Property"}
          className="w-full h-auto object-cover aspect-video"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            // Only set to placeholder if not already using placeholder and not already tracked
            if (!isPlaceholder(target.src)) {
              target.onerror = null;
              markAsPlaceholder(target.src);
              target.src = DEFAULT_IMAGE;
            }
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

        {/* Favorite Button */}
        <button
          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-sm transition-colors hover:bg-gray-50"
          onClick={onToggleFavorite}
        >
          <Heart
            className={`w-3 h-3 ${
              isFavorite ? "fill-red-500 text-red-500" : "text-gray-400"
            }`}
          />
        </button>

        {/* Status badges - Featured badge */}
        {isFeatured && (
          <div className="absolute top-10 right-2">
            <StatusBadge type="featured" size="small" />
          </div>
        )}

        {/* Boosted badge */}
        {isBoosted && (
          <div className="absolute top-2 right-10">
            <StatusBadge type="boosted" size="small" />
          </div>
        )}
      </div>
    );
  }
);

PropertyImage.displayName = "PropertyImage";

export default PropertyImage;
